/**
 * GoodGuardian — Email Verification Landing
 * Reached via the link in the verification email: /verify-email?token=xxx
 */
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Button from '@/components/common/Button'

export default function VerifyEmail() {
  const [params]   = useSearchParams()
  const navigate   = useNavigate()
  const token      = params.get('token')
  const [status, setStatus] = useState('loading')   // loading | success | error | notoken
  const [message, setMessage] = useState('')

  useEffect(() => {
    if (!token) { setStatus('notoken'); return }

    fetch('/api/auth/verify-email', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ token }),
    })
      .then(r => r.json())
      .then(data => {
        if (data.success) setStatus('success')
        else { setStatus('error'); setMessage(data.error || 'Verification failed.') }
      })
      .catch(() => { setStatus('error'); setMessage('Network error. Please try again.') })
  }, [token])

  return (
    <div className="min-h-screen brand-gradient flex items-center justify-center p-5">
      <div className="bg-white rounded-3xl p-8 max-w-sm w-full text-center shadow-2xl">
        <span className="text-5xl block mb-4">🛡️</span>
        <h1 className="text-xl font-extrabold text-slate-800 mb-1">GoodGuardian</h1>

        {status === 'loading' && (
          <>
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto my-6" />
            <p className="text-slate-500 text-sm">Verifying your email…</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto my-6">
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-lg font-bold text-slate-800 mb-2">Email Verified!</h2>
            <p className="text-slate-500 text-sm mb-6">
              Your email address has been confirmed. Your account is now fully secured.
            </p>
            <Button onClick={() => navigate('/dashboard')} className="w-full">
              Go to Dashboard
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto my-6">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-lg font-bold text-slate-800 mb-2">Verification Failed</h2>
            <p className="text-slate-500 text-sm mb-6">{message}</p>
            <Button onClick={() => navigate('/dashboard')} className="w-full">
              Go to Dashboard
            </Button>
          </>
        )}

        {status === 'notoken' && (
          <>
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto my-6">
              <span className="text-3xl">⚠️</span>
            </div>
            <h2 className="text-lg font-bold text-slate-800 mb-2">Invalid Link</h2>
            <p className="text-slate-500 text-sm mb-6">
              This verification link is missing its token. Please use the link from your verification email.
            </p>
            <Button onClick={() => navigate('/dashboard')} className="w-full">
              Return to App
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
